import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { RemediationProvider } from './context/RemediationContext';
import Layout from './components/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import { PERMISSIONS } from './data/roles';

import Dashboard from './pages/Dashboard';
import Employees from './pages/Employees';
import Learners from './pages/Learners';
import LearnerProfile from './pages/LearnerProfile';
import Phishing from './pages/Phishing';
import Campaigns from './pages/Campaigns';
import CampaignDetail from './pages/CampaignDetail';
import Library from './pages/Library';
import Reports from './pages/Reports';
import Compliance from './pages/Compliance';
import Notifications from './pages/Notifications';
import Settings from './pages/Settings';
import Remediation from './pages/Remediation';
import RoleManagement from './pages/admin/RoleManagement';
import Policies from './pages/admin/Policies';
import EmployeePortal from './pages/employee/EmployeePortal';
import EmployeeSettings from './pages/employee/EmployeeSettings';
import ManagerPortal from './pages/manager/ManagerPortal';

import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Callback from './pages/auth/Callback';
import PhishingLandingPage from './pages/PhishingLandingPage';
import SharedModule from './pages/SharedModule';
import PrivacyPolicy from './pages/PrivacyPolicy';

function AuthGuard({ children }) {
  const { authUser, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50 text-slate-500">Loading...</div>;
  }

  if (!authUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

function App() {
  return (
    <AuthProvider>
      <RemediationProvider>
        <Router>
          <Routes>
            {/* Public / Unauthenticated Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<Callback />} />
            <Route path="/phishing-awareness" element={<PhishingLandingPage />} />
            <Route path="/shared-module/:token" element={<SharedModule />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            
            {/* Protected Application Routes */}
            <Route path="/" element={<AuthGuard><Layout /></AuthGuard>}>
              <Route index element={<ProtectedRoute permission={PERMISSIONS.VIEW_FULL_DASHBOARD}><Dashboard /></ProtectedRoute>} />
              <Route path="employees"      element={<ProtectedRoute permission={PERMISSIONS.VIEW_EMPLOYEES}><Employees /></ProtectedRoute>} />
              <Route path="learners"       element={<ProtectedRoute permission={PERMISSIONS.VIEW_LEARNERS}><Learners /></ProtectedRoute>} />
              <Route path="learners/:id"   element={<ProtectedRoute permission={PERMISSIONS.VIEW_LEARNERS}><LearnerProfile /></ProtectedRoute>} />
              <Route path="phishing"       element={<ProtectedRoute permission={PERMISSIONS.VIEW_PHISHING}><Phishing /></ProtectedRoute>} />
              <Route path="campaigns"      element={<ProtectedRoute permission={PERMISSIONS.VIEW_CAMPAIGNS}><Campaigns /></ProtectedRoute>} />
              <Route path="campaigns/:id"  element={<ProtectedRoute permission={PERMISSIONS.VIEW_CAMPAIGNS}><CampaignDetail /></ProtectedRoute>} />
              <Route path="library"        element={<ProtectedRoute permission={PERMISSIONS.VIEW_LIBRARY}><Library /></ProtectedRoute>} />
              <Route path="reports"        element={<ProtectedRoute permission={PERMISSIONS.VIEW_OWN_REPORTS}><Reports /></ProtectedRoute>} />
              <Route path="compliance"     element={<ProtectedRoute permission={PERMISSIONS.VIEW_COMPLIANCE}><Compliance /></ProtectedRoute>} />
              <Route path="notifications"  element={<ProtectedRoute permission={PERMISSIONS.VIEW_NOTIFICATIONS}><Notifications /></ProtectedRoute>} />
              <Route path="remediation"    element={<ProtectedRoute permission={PERMISSIONS.VIEW_REMEDIATION}><Remediation /></ProtectedRoute>} />
              <Route path="settings"       element={<ProtectedRoute permission={PERMISSIONS.VIEW_SETTINGS}><Settings /></ProtectedRoute>} />
              <Route path="roles"          element={<ProtectedRoute permission={PERMISSIONS.MANAGE_SETTINGS}><RoleManagement /></ProtectedRoute>} />
              <Route path="policies"       element={<ProtectedRoute permission={PERMISSIONS.MANAGE_SETTINGS}><Policies /></ProtectedRoute>} />
              
              <Route path="my-team"        element={<ProtectedRoute permission={PERMISSIONS.VIEW_OWN_TEAM}><ManagerPortal /></ProtectedRoute>} />
              
              {/* Employee Dedicated Routes - Flattened for exact React Router compatibility */}
              <Route path="employee/dashboard" element={<ProtectedRoute permission={PERMISSIONS.VIEW_OWN_TRAINING}><EmployeePortal /></ProtectedRoute>} />
              <Route path="employee/my-history" element={<ProtectedRoute permission={PERMISSIONS.VIEW_OWN_REPORTS}><EmployeePortal /></ProtectedRoute>} />
              <Route path="employee/settings" element={<ProtectedRoute permission={PERMISSIONS.VIEW_OWN_TRAINING}><EmployeeSettings /></ProtectedRoute>} />

              {/* Legacy URL Redirects */}
              <Route path="my-training" element={<Navigate to="/employee/dashboard" replace />} />
              <Route path="employee/my-training" element={<Navigate to="/employee/dashboard" replace />} />
              <Route path="my-history" element={<Navigate to="/employee/my-history" replace />} />
            </Route>
          </Routes>
        </Router>
      </RemediationProvider>
    </AuthProvider>
  );
}

export default App;